import React, { useEffect, useState, useRef, useCallback } from "react"
import { CheckCircle2, AlertTriangle, XCircle, Info, Bell, X } from "lucide-react"

export type ToastType = "success" | "warning" | "danger" | "info" | "default"

export interface ToastData {
  id: string
  type: ToastType
  title?: string
  message: string
  autoClose?: number
}

const CONFIG = {
  success: {
    icon: <CheckCircle2 size={18} />,
    color: "text-emerald-400",
    hex: "#10b981",
    border: "border-emerald-500/20",
    title: "Sucesso",
  },
  warning: {
    icon: <AlertTriangle size={18} />,
    color: "text-amber-400",
    hex: "#fbbf24",
    border: "border-amber-500/20",
    title: "Atenção",
  },
  danger: {
    icon: <XCircle size={18} />,
    color: "text-rose-400",
    hex: "#fb7185",
    border: "border-rose-500/20",
    title: "Erro",
  },
  info: {
    icon: <Info size={18} />,
    color: "text-blue-400",
    hex: "#60a5fa",
    border: "border-blue-500/20",
    title: "Informação",
  },
  default: {
    icon: <Bell size={18} />,
    color: "text-zinc-400",
    hex: "#a1a1aa",
    border: "border-zinc-800",
    title: "Notificação",
  },
}

interface ToastProps extends ToastData {
  onClose: (id: string) => void
}

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  autoClose = 5000,
  onClose,
}) => {
  const [remaining, setRemaining] = useState(autoClose)
  const [isPaused, setIsPaused] = useState(false)
  const lastTick = useRef<number>(performance.now())
  const requestRef = useRef<number>()
  const config = CONFIG[type]

  const animate = useCallback(
    (time: number) => {
      const delta = time - lastTick.current
      lastTick.current = time

      if (!isPaused) {
        setRemaining((prev) => {
          const next = prev - delta
          if (next <= 0) {
            onClose(id)
            return 0
          }
          return next
        })
      }
      requestRef.current = requestAnimationFrame(animate)
    },
    [isPaused, id, onClose]
  )

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate)
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current)
    }
  }, [animate])

  // Cálculo da porcentagem para o preenchimento radial
  const percentage = (remaining / autoClose) * 100

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => {
        lastTick.current = performance.now()
        setIsPaused(false)
      }}
      className={`group pointer-events-auto relative flex w-80 items-center gap-4 rounded-xl border bg-zinc-950/80 p-4 shadow-2xl backdrop-blur-md transition-all duration-300 ${config.border} animate-in fade-in slide-in-from-right-5`}
    >
      <button
        onClick={() => onClose(id)}
        className={`${config.border} absolute left-[-10px] top-[-10px] rounded-full border bg-zinc-950/90 p-1 text-zinc-600 transition-colors hover:text-zinc-300`}
      >
        <X size={14} />
      </button>

      <div className={config.color}>{config.icon}</div>

      <div className="min-w-0 flex-1">
        <h4 className={`truncate text-sm font-bold ${config.color}`}>{title || config.title}</h4>
        <p className="line-clamp-2 text-xs text-zinc-400">{message}</p>
      </div>

      <div className="flex flex-col items-center justify-between self-stretch py-0.5">
        {/* Relógio Pie Style Corrigido */}
        <div
          className="relative flex h-5 w-5 items-center justify-center rounded-full bg-zinc-800"
          style={{
            background: `conic-gradient(${config.hex} ${percentage}%, transparent 0)`,
          }}
        >
          {/* Fundo interno para criar o efeito de borda/pie */}
          <div className="absolute inset-[2px] rounded-full bg-zinc-950" />

          {/* Opcional: Número bem pequeno no centro */}
          <span className="relative text-[7px] font-bold text-zinc-500">
            {Math.ceil(remaining / 1000)}
          </span>
        </div>
      </div>
    </div>
  )
}
